import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  TableContainer,
  Paper,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import FeedbackIcon from '@mui/icons-material/Feedback';
import { decryptText, decryptAppId } from "../utils/encryption";
import PageTitle from "../components/PageTitle";
import axios from "axios";

const ratingOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const Form45Feedback = () => {
  const location = useLocation();

  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());

  // API Data States
  const [assessorData, setAssessorData] = useState(null);
  const [assessorList, setAssessorList] = useState([]);
  const [selectedAssessor, setSelectedAssessor] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingAssessors, setLoadingAssessors] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Form States
  const [answers, setAnswers] = useState([]);
  const [ethics, setEthics] = useState("");
  const [ethicsRemarks, setEthicsRemarks] = useState("");
  const [confidentiality, setConfidentiality] = useState("");
  const [confidentialityRemarks, setConfidentialityRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch assessor list on component mount
  useEffect(() => {
    if (assessmentId && appId) {
      fetchAssessorList();
    }
  }, [assessmentId, appId]);

  // Fetch GetForm45Assesor API
  const fetchAssessorList = async () => {
    setLoadingAssessors(true);
    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetForm45AssesorLab?assessmentId=${assessmentId}&appid=${appId}`;
      console.log('📋 Fetching assessor list:', url);
      
      const response = await axios.get(url);
      console.log('✅ Assessor list response:', response.data);

      if (response.data && response.data.isstatus) {
        setAssessorData({
          labid: response.data.labid,
          labName: response.data.labName,
          visitType: response.data.visitType,
          visitDate: response.data.visitDate,
          feedbackforAsseesorId: response.data.feedbackforAsseesorId,
          feedbackforassessorName: response.data.feedbackforassessorName,
          capacity: response.data.capacity,
          assessmentDate: response.data.assessmentDate,
          standrad: response.data.standrad,
        });
        setAssessorList(response.data.assessor || []);
      } else {
        showSnackbar("Failed to load assessor list", "error");
      }
    } catch (error) {
      console.error('❌ Error fetching assessor list:', error);
      showSnackbar("Error loading assessor list", "error");
    } finally {
      setLoadingAssessors(false);
    }
  };

  // Fetch GetForm45Question API
  const fetchQuestions = async (peerAssessorId) => {
    if (!assessorData) {
      showSnackbar("Assessor data not loaded", "error");
      return;
    }

    setLoadingQuestions(true);
    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetForm45Question?appid=${appId}&labid=${assessorData.labid}&assessorid=${peerAssessorId}&labName=${encodeURIComponent(assessorData.labName)}&visitType=${encodeURIComponent(assessorData.visitType)}&visitDate=${assessorData.visitDate}&feedbackforId=${peerAssessorId}&feedbackby=${appId}&capacity=${encodeURIComponent(assessorData.capacity)}&peerAsessorId=${peerAssessorId}&loginname=${encodeURIComponent('Laboratory')}`;
      console.log('📋 Fetching questions:', url);
      
      const response = await axios.get(url);
      console.log('✅ Questions response:', response.data);

      if (response.data && response.data.isstatus) {
        const questionData = response.data.question || [];
        setQuestions(questionData);
        // Initialize answers array
        setAnswers(questionData.map(q => ({ marks: q.marks || "", remarks: q.remarks || "" })));
      } else {
        showSnackbar("Failed to load questions", "error");
      }
    } catch (error) {
      console.error('❌ Error fetching questions:', error);
      showSnackbar("Error loading questions", "error");
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Handle assessor selection
  const handleAssessorSelect = (assessor) => {
    setSelectedAssessor(assessor);
    fetchQuestions(assessor.id);
  };

 

  const handleChange = (index, field, value) => {
    const updated = [...answers];
    updated[index][field] = value;
    setAnswers(updated);
  };

  const getRatingColor = (rating) => {
    if (rating >= 9) return 'success';
    if (rating >= 6) return 'primary';
    if (rating >= 4) return 'warning';
    return 'error';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 9) return 'Excellent';
    if (rating >= 6) return 'Satisfactory';
    if (rating >= 4) return 'Needs Improvement';
    return 'Unsatisfactory';
  };

  const handleSubmit = async () => {
    if (!selectedAssessor) {
      showSnackbar("Please select an assessor first", "warning");
      return;
    }

    // Validate all questions are answered
    const allAnswered = answers.every(answer => answer.marks !== "");
    if (!allAnswered) {
      showSnackbar("Please provide ratings for all questions", "warning");
      return;
    }

    // Validate remarks for No responses
    if (ethics === "No" && !ethicsRemarks.trim()) {
      showSnackbar("Please provide remarks for Ethics assessment", "warning");
      return;
    }

    if (confidentiality === "No" && !confidentialityRemarks.trim()) {
      showSnackbar("Please provide remarks for Confidentiality assessment", "warning");
      return;
    }

    setLoading(true);
    try {
      // Prepare scoreTable
      const scoreTable = questions.map((q, index) => ({
        qid: q.id,
        score: String(answers[index].marks),
        remarks: answers[index].remarks || ""
      }));

      // Prepare payload
      const payload = {
        labId: assessorData.labid,
        labName: assessorData.labName,
        visitType: assessorData.visitType,
        visitDate: assessorData.visitDate,
        feedbackForId: localStorage.getItem("username") || "",
        feedbackForName: localStorage.getItem("username") || "",
        capacity: assessorData.capacity,
        feedbackBy: appId,
        peerAssessorId: selectedAssessor.id,
        peerAssessorName: selectedAssessor.assessorName,
        peerCapacity: selectedAssessor.capacity || "",
        insertBy: appId,
        appId: appId,
        assessmentType: "Main",
        feedbackGivenBy: "Laboratory",
        scoreTable: scoreTable,
        throughoutAssessment: ethics,
        throughoutRemarks: ethicsRemarks,
        confidentialityAssessment: confidentiality,
        confidentialityRemarks: confidentialityRemarks,
        assessmentid: assessmentId
      };

      console.log("📤 Submitting Feedback Payload:", payload);

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveFeedback`;
      const response = await axios.post(url, payload);
      
      console.log('✅ Save feedback response:', response.data);

      if (response.data && response.data.isstatus) {
        showSnackbar(response.data.message || "Feedback submitted successfully!", "success");
        
        // Reset form
        setAnswers(questions.map(() => ({ marks: "", remarks: "" })));
        setEthics("");
        setEthicsRemarks("");
        setConfidentiality("");
        setConfidentialityRemarks("");
        setSelectedAssessor(null);
        
        // Refresh assessor list
        fetchAssessorList();
      } else {
        showSnackbar(response.data?.message || "Failed to submit feedback", "error");
      }
    } catch (error) {
      console.error("❌ Error submitting feedback:", error);
      showSnackbar("Error submitting feedback. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="NABL | Feedback" />
      <Box sx={{ p: 2, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            ASSESSOR FEEDBACK
          </Typography>
        </Box>

        {/* Assessor Table */}
        <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <FeedbackIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Assessor Details
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Sr. No.
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Assessor Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Feedback
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingAssessors ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>
                          Loading assessors...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : assessorList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          No assessors found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assessorList.map((assessor, index) => (
                      <TableRow
                        key={assessor.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                          backgroundColor:
                            selectedAssessor?.id === assessor.id
                              ? "#e3f2fd"
                              : "inherit",
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: "medium", mb: 0.5 }}>
                            {assessor.assessorName}
                          </Typography>

                          {/* 🔥 capacity as Chip */}
                          <Chip
                            label={assessor.capacity}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />

                          <Typography
                            sx={{
                              fontWeight: "medium",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {assessor.discipline}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {assessor.feedbackGiven === "0" ? (
                            <Chip
                              label={
                                selectedAssessor?.id === assessor.id
                                  ? "Selected"
                                  : "Provide Feedback"
                              }
                              color={
                                selectedAssessor?.id === assessor.id
                                  ? "success"
                                  : "primary"
                              }
                              size="small"
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleAssessorSelect(assessor)}
                            />
                          ) : (
                            <Typography color="text.secondary">
                              Feedback already given, no action needed
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              assessor.feedbackGiven === "1"
                                ? "Completed"
                                : "Pending"
                            }
                            color={
                              assessor.feedbackGiven === "1"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Rating Guide */}
        <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2, display: "none" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}
            >
              Feedback Rating Guide
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Rating Range
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Performance Level
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Chip label="1 – 3" color="error" size="small" />
                    </TableCell>
                    <TableCell>Unsatisfactory</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Chip label="4 – 5" color="warning" size="small" />
                    </TableCell>
                    <TableCell>Needs Improvement</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Chip label="6 – 8" color="primary" size="small" />
                    </TableCell>
                    <TableCell>Satisfactory</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Chip label="9 – 10" color="success" size="small" />
                    </TableCell>
                    <TableCell>Excellent</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 3, color: "primary.main" }}
            >
              Performance Assessment
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                        width: "5%",
                      }}
                    >
                      S No.
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                        width: "40%",
                      }}
                    >
                      Evaluation Criteria
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                        width: "20%",
                      }}
                    >
                      Rating (1-10)
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                        width: "35%",
                      }}
                    >
                      Remarks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingQuestions ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>
                          Loading questions...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : questions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          {selectedAssessor
                            ? "No questions available"
                            : "Please select an assessor to load questions"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    questions.map((q, i) => (
                      <TableRow
                        key={q.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "#fafafa",
                          },
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <TableCell
                          sx={{ verticalAlign: "top", fontWeight: "medium" }}
                        >
                          {i + 1}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {q.question}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Select
                            size="small"
                            value={answers[i].marks}
                            onChange={(e) =>
                              handleChange(i, "marks", e.target.value)
                            }
                            displayEmpty
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="">--Select Rating--</MenuItem>
                            {ratingOptions.map((r) => (
                              <MenuItem key={r} value={r}>
                                {r} - {getRatingLabel(r)}
                              </MenuItem>
                            ))}
                          </Select>
                          {answers[i].marks && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={getRatingLabel(answers[i].marks)}
                                color={getRatingColor(answers[i].marks)}
                                size="small"
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={answers[i].remarks}
                            onChange={(e) =>
                              handleChange(i, "remarks", e.target.value)
                            }
                            placeholder="Enter your remarks here..."
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Essential Attributes */}
        <Card
          sx={{
            mb: 3,
            boxShadow: 2,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "primary.main",
              }}
            >
              Assessor Essential Attributes
            </Typography>

            {/* Ethics Question */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#333",
                }}
              >
                Ethical throughout the assessment
              </Typography>
              <RadioGroup
                row
                value={ethics}
                onChange={(e) => {
                  setEthics(e.target.value);
                  if (e.target.value === "Yes") {
                    setEthicsRemarks("");
                  }
                }}
                sx={{ mb: ethics === "No" ? 2 : 0 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={
                    <Radio
                      sx={{
                        color: "#4caf50",
                        "&.Mui-checked": { color: "#4caf50" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>Yes</Typography>}
                />
                <FormControlLabel
                  value="No"
                  control={
                    <Radio
                      sx={{
                        color: "#f44336",
                        "&.Mui-checked": { color: "#f44336" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>No</Typography>}
                />
              </RadioGroup>

              {ethics === "No" && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks (Required for No)"
                  placeholder="Please provide details..."
                  value={ethicsRemarks}
                  onChange={(e) => setEthicsRemarks(e.target.value)}
                  required
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#f44336",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f44336",
                      },
                    },
                  }}
                />
              )}
            </Box>

            {/* Confidentiality Question */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#333",
                }}
              >
                Confidentiality and Impartiality respected
              </Typography>
              <RadioGroup
                row
                value={confidentiality}
                onChange={(e) => {
                  setConfidentiality(e.target.value);
                  if (e.target.value === "Yes") {
                    setConfidentialityRemarks("");
                  }
                }}
                sx={{ mb: confidentiality === "No" ? 2 : 0 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={
                    <Radio
                      sx={{
                        color: "#4caf50",
                        "&.Mui-checked": { color: "#4caf50" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>Yes</Typography>}
                />
                <FormControlLabel
                  value="No"
                  control={
                    <Radio
                      sx={{
                        color: "#f44336",
                        "&.Mui-checked": { color: "#f44336" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>No</Typography>}
                />
              </RadioGroup>

              {confidentiality === "No" && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks (Required for No)"
                  placeholder="Please provide details..."
                  value={confidentialityRemarks}
                  onChange={(e) => setConfidentialityRemarks(e.target.value)}
                  required
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#f44336",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f44336",
                      },
                    },
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Save Button */}
        {assessorList.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                  Submitting...
                </>
              ) : (
                "Submit Feedback"
              )}
            </Button>
          </Box>
        )}

       
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Form45Feedback;
